import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { getTextClassName, NextArrow, PrevArrow } from "../../../utils";
import { useNavigate } from "react-router-dom";
import { useCarouselContext } from "../../../context/CarouselContext";
import "./CarouselSlide.css";
import LoaderCss from "../../ui/loader/LoaderCss";
import ramka from "../../../assets/ramka.png";
import menuPlaceholder from "../../../assets/menu-placeholder.webp";
import CustomVideoPlayer from "./CustomVideoPlayer";
import "../../../index.css";
import { PAGES_URLS } from "../../../config/pages-url.config";

const CarouselSlide = ({ currentCategory, carouselData = [] }) => {
  const {
    setCurrentSlide,
    currentSlide,
    nextCategory,
    prevCategory,
    setShowText,
    videoPreview,
    setVideoPreview,
    onlyImgLoaded,
    setOnlyImgLoaded,
    setShowPlayIcon,
    setVideoLoaded,
    isPlaying,
    setIsPlaying,
  } = useCarouselContext();
  const navigate = useNavigate();
  const swiperRef = useRef(null);
  const imageRefs = useRef({});

  const [startX, setStartX] = useState(null);
  const [ramkaLoaded, setRamkaLoaded] = useState(false);
  const [loadedImageSrcs, setLoadedImageSrcs] = useState(() => new Set());

  const filteredData = useCallback((data = [], currentCategory) => {
    const category = data.find(
      (item) => String(item.id) === String(currentCategory)
    );
    return category ? category.foods || [] : [];
  }, []);

  const foods = useMemo(
    () => filteredData(carouselData, currentCategory),
    [carouselData, currentCategory, filteredData]
  );

  const findCategoryWithFoods = useCallback(
    (direction) => {
      const currentIndex = carouselData.findIndex(
        (item) => String(item.id) === String(currentCategory)
      );

      if (currentIndex === -1 || carouselData.length < 2) {
        return null;
      }

      for (let step = 1; step < carouselData.length; step += 1) {
        const nextIndex =
          direction === "next"
            ? (currentIndex + step) % carouselData.length
            : (currentIndex - step + carouselData.length) % carouselData.length;
        const category = carouselData[nextIndex];

        if (
          String(category.id) !== String(currentCategory) &&
          Array.isArray(category.foods) &&
          category.foods.length > 0
        ) {
          return category;
        }
      }

      return null;
    },
    [carouselData, currentCategory]
  );

  const activeFood = foods[currentSlide];
  const activeImageSrc = activeFood?.image
    ? PAGES_URLS.BASE_URL + activeFood.image
    : menuPlaceholder;
  const activeMediaKey = `${currentCategory || ""}-${currentSlide}-${
    activeFood?.id || ""
  }-${activeFood?.image || ""}-${activeFood?.video || ""}`;

  useEffect(() => {
    if (foods.length === 0) {
      setCurrentSlide(0);
      return;
    }

    if (currentSlide > foods.length - 1) {
      const safeIndex = foods.length - 1;
      setCurrentSlide(safeIndex);
      swiperRef.current?.swiper?.slideTo(safeIndex, 0);
    }
  }, [currentSlide, foods.length, setCurrentSlide]);

  useEffect(() => {
    setVideoPreview(false);
    setVideoLoaded(false);
    setIsPlaying(false);
    setShowPlayIcon(false);
    setShowText(false);
    setOnlyImgLoaded(
      Boolean(activeImageSrc && loadedImageSrcs.has(activeImageSrc))
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMediaKey]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(currentSlide, 0);
    }
  }, [currentCategory, currentSlide]);

  useEffect(() => {
    if (onlyImgLoaded || videoPreview) {
      const timeout = setTimeout(() => {
        setShowText(true);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setShowText(false);
    }
  }, [onlyImgLoaded, videoPreview, setShowText]);

  const loadedImg = useCallback((src) => {
    if (!src) {
      return;
    }

    setTimeout(() => {
      setLoadedImageSrcs((prev) => {
        if (prev.has(src)) {
          return prev;
        }

        const next = new Set(prev);
        next.add(src);
        return next;
      });

      if (src === activeImageSrc) {
        setOnlyImgLoaded(true);
      }
    }, 0);
  }, [activeImageSrc, setOnlyImgLoaded]);

  useEffect(() => {
    if (!activeImageSrc) {
      return;
    }

    const imageElement = imageRefs.current[activeImageSrc];
    if (imageElement?.complete && imageElement.naturalWidth > 0) {
      loadedImg(activeImageSrc);
    }
  }, [activeImageSrc, loadedImg]);

  const handleSlideChange = (swiper) => {
    if (foods.length === 0) {
      setCurrentSlide(0);
      return;
    }

    const maxIndex = foods.length - 1;
    const safeIndex = Math.min(Math.max(swiper.activeIndex, 0), maxIndex);

    if (swiper.activeIndex !== safeIndex) {
      swiper.slideTo(safeIndex, 0);
    }

    setCurrentSlide(safeIndex);
  };

  const next = () => {
    if (!swiperRef.current?.swiper || foods.length === 0) {
      return;
    }

    const lastSlideIndex = foods.length - 1;
    if (currentSlide < lastSlideIndex) {
      swiperRef.current.swiper.slideTo(currentSlide + 1);
      setCurrentSlide(currentSlide + 1);
      setShowPlayIcon(true);
      return;
    }

    const nextFoodCategory = findCategoryWithFoods("next");
    if (currentSlide === lastSlideIndex && nextFoodCategory) {
      navigate(`?category=${nextFoodCategory.id}`);
      const timeout = setTimeout(() => {
        setCurrentSlide(0);
        swiperRef.current.swiper.slideTo(0, 0);
        setOnlyImgLoaded(false);
        setShowText(false);
      }, 1);
      return () => clearTimeout(timeout);
    }
  };

  const previous = () => {
    if (!swiperRef.current?.swiper || foods.length === 0) {
      return;
    }

    if (currentSlide > 0) {
      swiperRef.current.swiper.slideTo(currentSlide - 1);
      setCurrentSlide(currentSlide - 1);
      setShowPlayIcon(true);
      return;
    }

    const prevFoodCategory = findCategoryWithFoods("prev");
    if (currentSlide === 0 && prevFoodCategory) {
      const prevFoods = filteredData(carouselData, prevFoodCategory.id);
      if (prevFoods.length === 0) {
        console.error("Нет данных для предыдущей категории");
        return;
      }
      const lastSlideIndex = prevFoods.length - 1;
      navigate(`?category=${prevFoodCategory.id}`);

      const timeout = setTimeout(() => {
        setCurrentSlide(lastSlideIndex);
        

        setOnlyImgLoaded(false);
        setShowText(false);
      }, 0);
      return () => clearTimeout(timeout);
    }
  };

  const handleTouchStart = (e) => {
    setStartX(e.touches[0]?.clientX);
  };

  const handleTouchEnd = (e) => {
    const endX = e.changedTouches[0]?.clientX;
    const deltaX = endX - startX;
    const slideWidth = swiperRef.current?.swiper?.slides[0]?.offsetWidth;
    if (!slideWidth) {
      setStartX(null);
      return;
    }

    const threshold = slideWidth * 0.2;

    if (deltaX > threshold) {
      if (
        currentSlide === 0 &&
        prevCategory &&
        String(prevCategory) !== String(currentCategory)
      ) {
        previous();
      }
    } else if (deltaX < -threshold) {
      if (
        currentSlide === foods.length - 1 &&
        nextCategory &&
        String(nextCategory) !== String(currentCategory)
      ) {
        next();
      }
    }

    setStartX(null);
  };
  // console.log('foods', foods);
  return (
    <div className="relative w-full mx-auto hxsi:max-h-[80vh]">
      <div className="w-full">
        <div className="relative mx-auto max-w-[300px] xsi:max-w-[310px] sm:max-w-[335px] md:max-w-[350px] lg:max-w-[380px] menu-frame-shell">
          <Swiper
            spaceBetween={30}
            className="mySwiper h-full w-full"
            ref={swiperRef}
            onSlideChange={handleSlideChange}
          >
            {foods.map((food, index) => {
              const imageSrc = food?.image
                ? PAGES_URLS.BASE_URL + food.image
                : menuPlaceholder;
              const imageElement = imageRefs.current[imageSrc];
              const imageLoaded =
                loadedImageSrcs.has(imageSrc) ||
                Boolean(imageElement?.complete && imageElement.naturalWidth > 0);

              return (
                <SwiperSlide
                  className="relative h-full w-full"
                  key={`${currentCategory}-${index}`}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  {food?.video === null && (
                    <div className="menu-frame-media ombre">
                      <img
                        ref={(element) => {
                          if (element) {
                            imageRefs.current[imageSrc] = element;
                          }
                        }}
                        className={`${ramkaLoaded ? "visible" : "invisible"}`}
                        src={imageSrc}
                        alt={food?.name}
                        onLoad={() => loadedImg(imageSrc)}
                        onError={() => loadedImg(imageSrc)}
                      />
                    </div>
                  )}
                  {food?.video === null &&
                    currentSlide === index &&
                    !imageLoaded && <LoaderCss />}
                  {food?.video !== null && (
                    <CustomVideoPlayer
                      index={index}
                      ramkaLoaded={ramkaLoaded}
                      src={PAGES_URLS.BASE_URL + food?.video}
                      preview={imageSrc}
                    />
                  )}

                  {ramkaLoaded &&
                    currentSlide === index &&
                    !(food?.video !== null && isPlaying) && (
                    <div className="menu-frame-caption">
                      <p
                        className={`${getTextClassName(
                          food?.name
                        )} mb-[8px] font-adieu font-regular user-select-none`}
                      >
                        {food?.name}
                      </p>
                      <p className="user-select-none text-[14.07px] font-stolzl font-medium leading-[13.93px] tracking-[4%]">
                        {food?.price} сом
                      </p>
                    </div>
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>
          <img
            src={ramka && ramka}
            alt="Ramka"
            className="menu-frame-image"
            onLoad={() => setRamkaLoaded(true)}
          />
        </div>
      </div>
      <div
        className={`${foods.length > 0 ? "visible" : "hidden"} ${
          ramkaLoaded ? "visible" : "hidden"
        } absolute top-[44.65%] left-[4.45%] z-20 select-none opacity-70`}
        onClick={previous}
      >
        <PrevArrow />
      </div>

      <div
        className={`${foods.length > 0 ? "visible" : "hidden"} ${
          ramkaLoaded ? "visible" : "hidden"
        } absolute top-[44.65%] right-[4.45%] z-20 opacity-70`}
        onClick={next}
      >
        <NextArrow />
      </div>
    </div>
  );
};

export default CarouselSlide;
